/* eslint-disable no-param-reassign */
import get from 'lodash/get';
import cookie from 'cookie';
import catchify from 'catchify';

import {
  USE_NEXT_PDP_EXPERIMENT_NAME,
  USE_NEXT_QUESTION_REVIEW_EXPERIMENT_NAME,
} from '@/constants/global';

import { isPDP, isIndexablePage } from '~/util/global';

const initialState = () => ({
  autocompleteQuery: '',
  autocompleteProductType: null,
  autocompleteGroup: null,
  session: {
    cart: {},
  },
  csrfToken: '',
  showBodyOverlay: false,
});

export { initialState as state };

export const getters = {
  userSession: (state) => state.session.user,
  button: (state) => get(state, 'session.session.button', null),
  campaigns: (state) => get(state, 'session.session.campaigns', []),
  showBodyOverlay: (state) => state.showBodyOverlay,
};

export const mutations = {
  setCart(state, data) {
    state.session.cart = data;
  },

  setSession(state, data) {
    const [session, cookies] = data;
    state.session = session;

    // csrf cookie not created for some reason when hitting /browse as a anon new user.  Hack!
    // csrf needed for Experiments
    if (cookies) {
      state.csrfToken = cookies.find((c) => {
        const curCookie = cookie.parse(c);

        if (curCookie.csrftoken) {
          return c;
        }

        return false;
      }) || '';
    }
  },
  setAutocompleteQuery(state, data) {
    state.autocompleteQuery = data;
  },
  setAutocompleteProductType(state, data) {
    state.autocompleteProductType = data;
  },
  setAutocompleteGroup(state, data) {
    state.autocompleteGroup = data;
  },
  setUserAvatar(state, data) {
    state.session.user.avatar = data;
    state.session.user.avatar_small = `${data}?width=22&height=22&aspect_ratio=1:1`;
  },
  setShowBodyOverlay(state, data) {
    state.showBodyOverlay = data;
  },
  setMessages(state, data) {
    state.session.messages = data;
  },
  setPlusDiscountPercentage(state, data) {
    state.session.plus_percentage_off = data;
  },
};

// When the user visits a page nuxtServerInit runs on the server side which performs
// the initial enroll for global experiments and then serializes the resulting variants
// in the store across to the client.
//
// gtm-spa will handle re-issueing enrolls on navigation changes
export const actions = {
  async nuxtServerInit(context, { req, res, ssrContext }) {
    // Get variants in experiments before anything so the variants are available
    // for nextExperimentRedirects
    await context.dispatch('experiments/getVariantsWithoutEnrolling', [
      USE_NEXT_PDP_EXPERIMENT_NAME,
      USE_NEXT_QUESTION_REVIEW_EXPERIMENT_NAME,
    ]);

    await context.dispatch('nextExperimentsRedirects', { req, res, ssrContext });
    await Promise.all([
      context.dispatch('browse/fetch'),
      context.dispatch('experiments/enroll'),
    ]);
  },
  async nuxtClientInit() {
    // Do nothing, prevent error on client side
  },
  // Since this is called by nuxtServerInit, then it also has the same behavior, is being called
  // on the server side, and it was also added to gtm-spa to be called on the client side
  // the parameters are validated so we can handle both server and client side calls
  async nextExperimentsRedirects(context, { req, res, ssrContext } = {}) {
    const path = req ? req.url.split('?')[0] : typeof window !== 'undefined' && window.location.pathname;
    // If path has no trailing slash, add it for comparison
    const comparisonPath = path && path.endsWith && path.endsWith('/') ? path : `${path}/`;
    const queryParams = req ? req.url.split('?')[1] : typeof window !== 'undefined' && window.location.search;
    // Recombine path and query params
    const url = queryParams ? `${path}?${queryParams}` : path;
    const redirectFn = (localUrl) => {
      // If we're in dev mode, do not redirect as we will infinitely redirect
      if (process.env.NODE_ENV === 'development') {
        return Promise.resolve(
          // eslint-disable-next-line no-console
          console.log(`[nextExperimentsRedirects] redirecting to ${localUrl}`),
        );
      }
      if (typeof window !== 'undefined') {
        window.location.href = localUrl;
      } else {
        ssrContext.redirected = true;
        res.writeHead(302, {
          Location: localUrl,
        });
        res.end();
      }
      return Promise.resolve();
    };

    if (!url || !redirectFn) return Promise.resolve();

    const getExperimentVariant = context.getters['experiments/getExperimentVariant'];
    const isNextPDPEnabled = getExperimentVariant(USE_NEXT_PDP_EXPERIMENT_NAME) === 'enabled';
    const isIndexableQnREnabled = getExperimentVariant(USE_NEXT_QUESTION_REVIEW_EXPERIMENT_NAME) === 'enabled';

    if ((isPDP(comparisonPath) && isNextPDPEnabled)
    || (isIndexablePage(comparisonPath) && isIndexableQnREnabled)) {
      return redirectFn(url);
    }

    return Promise.resolve();
  },
  async updateCartCount({ commit }) {
    const { $axios, $router } = this;
    const params = {
      ...$router.query,
      format: 'json',
    };

    const [err, response] = await catchify($axios.get('/api/sessions/current/', { params }));

    if (err) {
      // eslint-disable-next-line no-console
      console.log('error fetching session for store', err);
      return;
    }

    commit('setCart', response.data.cart);
  },

  setShowBodyOverlay({ commit }, data) {
    commit('setShowBodyOverlay', data);
  },

  clearSessionMessages({ commit }) {
    commit('setMessages', []);
  },
};
